import React from 'react';
import type { AeQuestionDisplayConfig } from '@/types/assessmentEngine';
import {
  AE_CHOICE_DEFAULT,
  AE_CHOICE_NA_SELECTED,
  AE_CHOICE_SELECTED,
  AE_EDITABLE_INPUT,
} from '@/components/assessment-engine/layout/aeTheme';
import { EMOJI_SCALE, resolveScaleEndpointLabels, ScaleDisplayStyle, scalePoints } from './scaleUtils';

export interface ScaleInputProps {
  min: number;
  max: number;
  display: AeQuestionDisplayConfig;
  value: number | null | undefined;
  isNa?: boolean;
  allowNa?: boolean;
  disabled?: boolean;
  /**
   * Unique HTML radio `name` for this control. Required when multiple scale
   * questions share the page — shared names cause the browser to clear other
   * questions' selections when one option is chosen.
   */
  name?: string;
  onChange: (value: number | null, isNa: boolean) => void;
}

function pickEmoji(index: number, total: number): string {
  const idx = Math.round((index / Math.max(total - 1, 1)) * (EMOJI_SCALE.length - 1));
  return EMOJI_SCALE[idx] ?? '😐';
}

/**
 * Uses the same equal-width columns as the scored values so each endpoint label
 * stays directly above its corresponding value, regardless of option count.
 */
function ScaleEndpointLabelRow({
  display,
  pointCount = 2,
}: {
  display: AeQuestionDisplayConfig;
  pointCount?: number;
}) {
  const { scaleMinLabel, scaleMaxLabel } = resolveScaleEndpointLabels(display);
  return (
    <div className="flex w-full text-xs text-[var(--color-secondary-text)]">
      {Array.from({ length: Math.max(pointCount, 2) }, (_, index) => (
        <span
          key={index}
          className="min-w-0 flex-1 text-center"
          data-scale-endpoint={index === 0 ? 'min' : index === pointCount - 1 ? 'max' : undefined}
        >
          {index === 0 ? scaleMinLabel : index === pointCount - 1 ? scaleMaxLabel : ''}
        </span>
      ))}
    </div>
  );
}

function ScaleNaButton({
  isNa,
  disabled,
  onChange,
}: {
  isNa?: boolean;
  disabled?: boolean;
  onChange: (value: number | null, isNa: boolean) => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={isNa ? AE_CHOICE_NA_SELECTED : AE_CHOICE_DEFAULT}
      onClick={() => onChange(null, true)}
    >
      N/A
    </button>
  );
}

/** Wraps scored values + endpoint labels; optional N/A is aligned beside (not under) High. */
function ScaleValueCluster({
  display,
  allowNa,
  isNa,
  disabled,
  onChange,
  children,
}: {
  display: AeQuestionDisplayConfig;
  allowNa?: boolean;
  isNa?: boolean;
  disabled?: boolean;
  onChange: (value: number | null, isNa: boolean) => void;
  children: React.ReactNode;
}) {
  const pointCount = React.Children.count(children);
  // When per-value labels are defined, each point already shows its own label,
  // so the Low/High endpoint row is redundant and hidden.
  const hasCustomValueLabels = (display.scaleValueLabels ?? []).some(
    (label) => (label ?? '').trim().length > 0
  );

  return (
    <div className="mt-2 flex w-full items-end gap-2 overflow-x-auto pb-1">
      <div className="flex min-w-[28rem] flex-1 flex-col gap-1">
        {!hasCustomValueLabels && (
          <ScaleEndpointLabelRow display={display} pointCount={pointCount} />
        )}
        <div className="flex w-full">
          {React.Children.map(children, (child, index) => (
            <div
              className="flex min-w-0 flex-1 justify-center"
              data-scale-value-position={
                index === 0 ? 'min' : index === pointCount - 1 ? 'max' : undefined
              }
            >
              {child}
            </div>
          ))}
        </div>
      </div>
      {allowNa && <ScaleNaButton isNa={isNa} disabled={disabled} onChange={onChange} />}
    </div>
  );
}

/** Shared scale control — numbers, radio, slider, emoji, or stars. */
export const ScaleInput: React.FC<ScaleInputProps> = ({
  min,
  max,
  display,
  value,
  isNa,
  allowNa,
  disabled,
  name,
  onChange,
}) => {
  const points = scalePoints(min, max);
  const style: ScaleDisplayStyle = display.scaleDisplayStyle ?? 'numbers';
  const valueLabels = display.scaleValueLabels ?? [];
  const endpoints = resolveScaleEndpointLabels(display);
  // Prefer caller-supplied question id so each scale is its own radio group.
  const radioGroupName = name ?? `scale-${min}-${max}`;

  if (style === 'slider') {
    const v = value ?? min;
    return (
      <div className="mt-2 space-y-2">
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          disabled={disabled}
          value={v}
          className="w-full accent-[var(--color-accent-bg)]"
          onChange={(e) => onChange(Number(e.target.value), false)}
        />
        <div className="flex justify-between text-sm text-[var(--color-surface-text)]">
          <span>{valueLabels[0] ?? endpoints.scaleMinLabel}</span>
          <span className="font-medium">{v}</span>
          <span>{valueLabels[valueLabels.length - 1] ?? endpoints.scaleMaxLabel}</span>
        </div>
      </div>
    );
  }

  if (style === 'stars') {
    return (
      <ScaleValueCluster
        display={display}
        allowNa={allowNa}
        isNa={isNa}
        disabled={disabled}
        onChange={onChange}
      >
        {points.map((n, i) => (
          <button
            key={n}
            type="button"
            disabled={disabled}
            className={`text-xl ${value !== null && value !== undefined && n <= value ? 'text-[var(--color-warning)]' : 'text-[var(--color-border)]'}`}
            onClick={() => onChange(n, false)}
            title={valueLabels[i] ?? String(n)}
          >
            ★
          </button>
        ))}
      </ScaleValueCluster>
    );
  }

  if (style === 'emoji') {
    return (
      <ScaleValueCluster
        display={display}
        allowNa={allowNa}
        isNa={isNa}
        disabled={disabled}
        onChange={onChange}
      >
        {points.map((n, i) => (
          <button
            key={n}
            type="button"
            disabled={disabled}
            className={`text-2xl p-1 rounded ${value === n ? 'ring-2 ring-[var(--color-accent-bg)]' : ''}`}
            onClick={() => onChange(n, false)}
            title={valueLabels[i] ?? String(n)}
          >
            {pickEmoji(i, points.length)}
          </button>
        ))}
      </ScaleValueCluster>
    );
  }

  const useRadio = style === 'radio';
  const btnClass = (selected: boolean) => (selected ? AE_CHOICE_SELECTED : AE_CHOICE_DEFAULT);

  return (
    <ScaleValueCluster
      display={display}
      allowNa={allowNa}
      isNa={isNa}
      disabled={disabled}
      onChange={onChange}
    >
      {points.map((n, i) =>
        useRadio ? (
          <label key={n} className="flex items-center gap-1 cursor-pointer text-sm">
            <input
              type="radio"
              name={radioGroupName}
              disabled={disabled}
              checked={value === n}
              onChange={() => onChange(n, false)}
              className="accent-[var(--color-accent-bg)]"
            />
            <span>{valueLabels[i] ?? n}</span>
          </label>
        ) : (
          <button
            key={n}
            type="button"
            disabled={disabled}
            className={btnClass(value === n)}
            onClick={() => onChange(n, false)}
            title={valueLabels[i] ?? String(n)}
          >
            {valueLabels[i] ?? n}
          </button>
        )
      )}
    </ScaleValueCluster>
  );
};

/**
 * @deprecated Labels are rendered inside ScaleInput. Kept for matrix header alignment helpers.
 */
export const ScaleEndpointLabels: React.FC<{ display: AeQuestionDisplayConfig }> = ({ display }) => (
  <ScaleEndpointLabelRow display={display} />
);

/** Per-value label inputs for builder settings. */
export const ScaleValueLabelsEditor: React.FC<{
  min: number;
  max: number;
  labels: string[];
  onChange: (labels: string[]) => void;
}> = ({ min, max, labels, onChange }) => {
  const points = scalePoints(min, max);
  return (
    <div className="space-y-1">
      {points.map((n, i) => (
        <div key={n} className="flex items-center gap-2 text-sm">
          <span className="w-8 text-[var(--color-secondary-text)]">{n}</span>
          <input
            className={`${AE_EDITABLE_INPUT} flex-1`}
            value={labels[i] ?? ''}
            placeholder={`Label for ${n}`}
            onChange={(e) => {
              const next = [...labels];
              next[i] = e.target.value;
              onChange(next);
            }}
          />
        </div>
      ))}
    </div>
  );
};
